import { useMemo } from 'react';
import { SpeakerAnalysis, SpeakerCorrectionRecord } from '../types/speakerCorrection';

export interface SpeakerAnalysisDerivedState {
  flaggedCount: number;
  hasUnresolvedFlags: boolean;
  unresolvedSuspectedMerges: SpeakerAnalysis['suspectedMerges'];
  lowConfidenceSpeakers: string[];
}

const LOW_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Derives the "flagged"-vs-"resolved" state for a single encounter from
 * L1's speaker_analysis blob plus the list of corrections persisted by L2.
 *
 * @param analysis        L1 output (transcripts.speaker_analysis)
 * @param corrections     L2 corrections fetched via listSpeakerCorrections
 * @param indexToSegmentId  Map of 1-based segment index (used in suspectedMerges)
 *                          to transcript_segments.id (UUIDs used in corrections).
 *                          Caller builds this from the encounter's segments.
 */
export function useSpeakerAnalysis(
  analysis: SpeakerAnalysis | null | undefined,
  corrections: SpeakerCorrectionRecord[],
  indexToSegmentId: Map<number, string> = new Map()
): SpeakerAnalysisDerivedState {
  return useMemo(() => {
    if (!analysis) {
      return {
        flaggedCount: 0,
        hasUnresolvedFlags: false,
        unresolvedSuspectedMerges: [],
        lowConfidenceSpeakers: [],
      };
    }

    const lowConfidenceSpeakers = analysis.speakers
      .filter((s) => s.confidence < LOW_CONFIDENCE_THRESHOLD)
      .map((s) => s.speakerId);

    const correctedSegmentIds = new Set<string>();
    const skippedMergeIds = new Set<string>();
    const relabeledSpeakerIds = new Set<string>();
    for (const c of corrections) {
      if (c.actionType === 'reassign' || c.actionType === 'split') {
        c.segmentIds.forEach((id) => correctedSegmentIds.add(id));
      } else if (c.actionType === 'relabel') {
        // beforeState keys are the speakerIds that were relabeled.
        Object.keys(c.beforeState ?? {}).forEach((sid) => relabeledSpeakerIds.add(sid));
      } else if (c.actionType === 'skip_with_reason') {
        const id = (c.beforeState as Record<string, unknown>)?.suspectedMergeId;
        if (typeof id === 'string') skippedMergeIds.add(id);
      }
    }

    const unresolvedSuspectedMerges = analysis.suspectedMerges.filter((m) => {
      const mergeId = `${m.azureSpeakerId}:[${m.segmentIndices.join(',')}]`;
      if (skippedMergeIds.has(mergeId)) return false;
      if (relabeledSpeakerIds.has(m.azureSpeakerId)) return false;
      // Resolved if any of its segmentIndices was touched by a correction.
      const anyCorrected = m.segmentIndices.some((idx) => {
        const segUuid = indexToSegmentId.get(idx);
        return segUuid !== undefined && correctedSegmentIds.has(segUuid);
      });
      return !anyCorrected;
    });

    const flaggedCount = analysis.suspectedMerges.length + lowConfidenceSpeakers.length;
    // Sign-block gate: only triggered by unresolved suspected merges, not low-confidence speakers.
    const hasUnresolvedFlags = unresolvedSuspectedMerges.length > 0;

    return {
      flaggedCount,
      hasUnresolvedFlags,
      unresolvedSuspectedMerges,
      lowConfidenceSpeakers,
    };
  }, [analysis, corrections, indexToSegmentId]);
}
